import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { UnitDetail } from "../api/types";
import { removalReasonLabel } from "../lib/tenants";
import { UNIT_STATUSES, unitStatusBadge, unitStatusLabel } from "../lib/units";
import { formatDate, formatDateTime } from "../lib/workorders";

export function UnitDetailPage() {
  const { id } = useParams();
  const unitId = Number(id);

  const unitQ = useQuery({
    queryKey: ["unit", unitId],
    queryFn: () => api.units.get(unitId),
    enabled: Number.isInteger(unitId),
  });

  if (unitQ.isLoading) {
    return (
      <Layout title="Unit">
        <p className="sub">Loading...</p>
      </Layout>
    );
  }
  if (unitQ.isError || !unitQ.data) {
    return (
      <Layout title="Unit">
        <section className="card">
          <p>That unit could not be found.</p>
          <Link to="/units" className="btn btn-ghost btn-xl">
            Back to Units
          </Link>
        </section>
      </Layout>
    );
  }

  return <UnitDetailView unit={unitQ.data.unit} />;
}

function UnitDetailView({ unit }: { unit: UnitDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [unitNumber, setUnitNumber] = useState(unit.unitNumber);
  const [floor, setFloor] = useState(unit.floor ?? "");
  const [status, setStatus] = useState(unit.status);
  const [appliances, setAppliances] = useState(unit.appliances ?? "");
  const [maintenanceNeeded, setMaintenanceNeeded] = useState(unit.maintenanceNeeded ?? "");
  const [notes, setNotes] = useState(unit.notes ?? "");
  const [saved, setSaved] = useState(false);

  const saveMut = useMutation({
    mutationFn: () =>
      api.units.update(unit.id, {
        unitNumber: unitNumber.trim(),
        floor: floor.trim() || null,
        status,
        appliances: appliances.trim() || null,
        maintenanceNeeded: maintenanceNeeded.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["unit", unit.id] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
    },
  });

  return (
    <Layout title="Unit Profile">
      <section className="card form-card">
        <div className="detail-head">
          <div>
            <h2>Unit {unit.unitNumber}</h2>
            <p className="sub">{unit.buildingName}</p>
          </div>
          <span className={`badge badge-${unitStatusBadge(unit.status)}`}>
            {unitStatusLabel(unit.status)}
          </span>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Unit number</span>
            <input value={unitNumber} onChange={(e) => setUnitNumber(e.target.value)} />
          </label>
          <label className="field">
            <span>Floor</span>
            <input value={floor} onChange={(e) => setFloor(e.target.value)} />
          </label>
        </div>

        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {UNIT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {unitStatusLabel(s)}
              </option>
            ))}
          </select>
        </label>

        <div className="two-col">
          <label className="field">
            <span>Appliances</span>
            <textarea
              rows={4}
              value={appliances}
              onChange={(e) => setAppliances(e.target.value)}
              placeholder="Fridge, stove, microwave, washer..."
            />
          </label>
          <label className="field">
            <span>Maintenance needed</span>
            <textarea
              rows={4}
              value={maintenanceNeeded}
              onChange={(e) => setMaintenanceNeeded(e.target.value)}
              placeholder="Known repairs, inspections, appliance issues..."
            />
          </label>
        </div>

        <label className="field">
          <span>Unit notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="form-actions">
          {saved && !saveMut.isPending && <span className="saved-note">Saved.</span>}
          {saveMut.isError && <span className="field-error">Could not save. Try again.</span>}
          <button className="btn btn-ghost btn-xl" onClick={() => navigate("/units")}>
            Back to Units
          </button>
          <button
            className="btn btn-primary btn-xl"
            onClick={() => {
              setSaved(false);
              saveMut.mutate();
            }}
            disabled={saveMut.isPending || !unitNumber.trim()}
          >
            {saveMut.isPending ? "Saving..." : "Save unit"}
          </button>
        </div>
      </section>

      <section className="two-col">
        <div className="card">
          <h2>Current tenant</h2>
          {unit.currentTenant ? (
            <p>
              <Link to={`/tenants/${unit.currentTenant.id}`} className="wo-title-link">
                {unit.currentTenant.firstName} {unit.currentTenant.lastName}
              </Link>
              {unit.currentTenant.moveInDate ? ` since ${formatDate(unit.currentTenant.moveInDate)}` : ""}
            </p>
          ) : (
            <p className="sub">No active tenant assigned.</p>
          )}
        </div>

        <div className="card">
          <h2>Past tenants</h2>
          {unit.tenantHistory.length === 0 ? (
            <p className="sub">No tenant residency history yet.</p>
          ) : (
            <ul className="timeline">
              {unit.tenantHistory.map((t) => (
                <li key={t.id}>
                  <Link to={`/tenants/${t.id}`} className="wo-title-link">
                    {t.name}
                  </Link>{" "}
                  {formatDate(t.moveInDate)} - {t.moveOutDate ? formatDate(t.moveOutDate) : "Current"}
                  {t.removalReason ? ` (${removalReasonLabel(t.removalReason)})` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="card">
        <h2>Work orders and incidents</h2>
        {unit.history.length === 0 ? (
          <p className="sub">No work orders or incidents tied to this unit yet.</p>
        ) : (
          <ul className="timeline">
            {unit.history.map((h) => (
              <li key={`${h.kind}-${h.id}`}>
                <strong>{formatDateTime(h.date)}</strong> -{" "}
                {h.kind === "work_order" ? "Work order" : "Incident"}: {h.title} ({h.status})
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Inventory used on this unit</h2>
        {unit.inventoryUsed.length === 0 ? (
          <p className="sub">No inventory has been recorded on unit work orders.</p>
        ) : (
          <ul className="timeline">
            {unit.inventoryUsed.map((item) => (
              <li key={item.id}>
                <strong>{formatDateTime(item.usedAt)}</strong> - {item.itemName}:{" "}
                {item.quantityUsed} {item.unitOfMeasure ?? ""} on{" "}
                <Link to={`/work-orders/${item.workOrderId}`}>{item.workOrderTitle}</Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Layout>
  );
}
